import { useState, useEffect, useRef, useCallback } from 'react'

/**
 * Custom hook for smooth character-by-character text streaming.
 * Adapts reveal speed to the incoming text rate so the animation
 * never fully catches up during streaming (no stop-start blotchiness).
 *
 * @param {string} incomingText - The full text received so far
 * @param {number} charsPerFrame - Fallback chars per frame (used for catch-up after stream ends)
 * @returns {{ displayedText: string, isAnimating: boolean, skipToEnd: () => void }}
 */
export function useStreamingText(incomingText, charsPerFrame = 6) {
  const [displayedText, setDisplayedText] = useState('')
  const [isAnimating, setIsAnimating] = useState(false)
  const incomingTextRef = useRef('')
  const animationFrameRef = useRef(null)
  const displayIndexRef = useRef(0)
  const lastRenderTimeRef = useRef(0)
  const skipRequestedRef = useRef(false)
  const isRunningRef = useRef(false)
  const streamDoneRef = useRef(false)

  // Rate tracking
  const chunkTimesRef = useRef([])      // timestamps of recent chunk arrivals
  const chunkSizesRef = useRef([])      // sizes of recent chunks
  const lastIncomingLenRef = useRef(0)

  const runAnimation = useCallback(() => {
    if (isRunningRef.current) return
    isRunningRef.current = true

    const animate = (timestamp) => {
      if (skipRequestedRef.current) {
        isRunningRef.current = false
        setIsAnimating(false)
        return
      }

      // ~30fps — slower frame rate keeps animation smooth without catching up too fast
      if (timestamp - lastRenderTimeRef.current < 33) {
        animationFrameRef.current = requestAnimationFrame(animate)
        return
      }
      lastRenderTimeRef.current = timestamp

      const currentIndex = displayIndexRef.current
      const targetText = incomingTextRef.current
      const backlog = targetText.length - currentIndex

      if (currentIndex < targetText.length) {
        setIsAnimating(true)

        let charsToReveal

        if (streamDoneRef.current) {
          // Stream ended — drain remaining text quickly (~0.5s)
          charsToReveal = Math.max(charsPerFrame * 2, Math.ceil(backlog / 5))
        } else {
          // During streaming: calculate rate from recent chunks
          // Aim to reveal at ~80% of incoming rate so we stay behind
          const chunks = chunkTimesRef.current
          const sizes = chunkSizesRef.current
          if (chunks.length >= 2) {
            const timeSpan = chunks[chunks.length - 1] - chunks[0]
            const totalChars = sizes.reduce((a, b) => a + b, 0)
            if (timeSpan > 0) {
              const incomingRate = totalChars / timeSpan  // chars per ms
              const targetRate = incomingRate * 0.85       // reveal at 85% of incoming
              charsToReveal = Math.max(2, Math.round(targetRate * 33))  // chars per 33ms frame
            } else {
              charsToReveal = charsPerFrame
            }
          } else {
            charsToReveal = charsPerFrame
          }

          // If backlog is very large, speed up to prevent falling too far behind
          if (backlog > 400) {
            charsToReveal = Math.max(charsToReveal, charsPerFrame * 3)
          } else if (backlog > 200) {
            charsToReveal = Math.max(charsToReveal, charsPerFrame * 2)
          }
        }

        // Word boundary: finish the word if close
        const remaining = targetText.slice(currentIndex)
        const nextSpace = remaining.indexOf(' ')
        const nextNewline = remaining.indexOf('\n')
        if (nextSpace > 0 && nextSpace <= charsToReveal + 3) {
          charsToReveal = nextSpace + 1
        } else if (nextNewline > 0 && nextNewline <= charsToReveal + 3) {
          charsToReveal = nextNewline + 1
        }

        // Markdown tokens: reveal complete formatting at once
        const nextChar = targetText[currentIndex]
        if (nextChar === '*' || nextChar === '_' || nextChar === '`' || nextChar === '#') {
          const match = remaining.match(/^(\*\*?|__?|`+|#+)([^*_`#\n]*?)(\1)/)
          if (match) {
            charsToReveal = Math.max(charsToReveal, match[0].length)
          }
        }

        displayIndexRef.current = Math.min(currentIndex + charsToReveal, targetText.length)
        setDisplayedText(targetText.slice(0, displayIndexRef.current))

        animationFrameRef.current = requestAnimationFrame(animate)
      } else {
        // Caught up — wait one more frame in case more text arrives
        animationFrameRef.current = requestAnimationFrame((ts) => {
          if (displayIndexRef.current < incomingTextRef.current.length) {
            animate(ts)
          } else {
            animationFrameRef.current = null
            isRunningRef.current = false
            setIsAnimating(false)
          }
        })
      }
    }

    animationFrameRef.current = requestAnimationFrame(animate)
  }, [charsPerFrame])

  const skipToEnd = useCallback(() => {
    skipRequestedRef.current = true
    isRunningRef.current = false
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    displayIndexRef.current = incomingTextRef.current.length
    setDisplayedText(incomingTextRef.current)
    setIsAnimating(false)
  }, [])

  // When new text arrives, track rate and ensure animation is running
  useEffect(() => {
    const prevLen = lastIncomingLenRef.current
    incomingTextRef.current = incomingText
    lastIncomingLenRef.current = incomingText.length

    if (incomingText.length > prevLen) {
      const now = performance.now()
      const chunkSize = incomingText.length - prevLen
      chunkTimesRef.current.push(now)
      chunkSizesRef.current.push(chunkSize)
      // Keep last 8 chunks for rate calculation
      if (chunkTimesRef.current.length > 8) {
        chunkTimesRef.current.shift()
        chunkSizesRef.current.shift()
      }
      streamDoneRef.current = false
    }

    if (skipRequestedRef.current) {
      displayIndexRef.current = incomingText.length
      setDisplayedText(incomingText)
      return
    }

    if (displayIndexRef.current < incomingText.length) {
      runAnimation()
    }
  }, [incomingText, runAnimation])

  // Detect stream end: if no new text for 600ms, switch to catch-up mode
  useEffect(() => {
    if (!incomingText || streamDoneRef.current) return

    const timer = setTimeout(() => {
      if (lastIncomingLenRef.current === incomingText.length) {
        streamDoneRef.current = true
      }
    }, 600)

    return () => clearTimeout(timer)
  }, [incomingText])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      isRunningRef.current = false
    }
  }, [])

  // Reset when text resets (new message)
  useEffect(() => {
    if (!incomingText) {
      displayIndexRef.current = 0
      lastIncomingLenRef.current = 0
      setDisplayedText('')
      setIsAnimating(false)
      skipRequestedRef.current = false
      streamDoneRef.current = false
      isRunningRef.current = false
      chunkTimesRef.current = []
      chunkSizesRef.current = []
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }
  }, [incomingText])

  return { displayedText, isAnimating, skipToEnd }
}
